import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Icon from '../../components/common/Icon';
import BravosQuestions from './BravosQuestions';
import './BravosSurveyFlow.css';

/* Paginated Bravos survey flow — one question per screen with admin-managed quote/visual cards
   interleaved, a progress bar, and per-page validation. Reused by the public Company Readiness
   Scan and the public Employee Pulse. Renders each question via the shared BravosQuestions
   renderer (a 1-question subset), so the field widgets stay identical to the member surveys.
   Calls onComplete() after the last step (the caller then shows its contact/consent/results step). */

const isRequired = (q) => !!q && (q.type === 'choice' || q.type === 'scale');
const isAnswered = (v) => v !== undefined && v !== null && v !== '';

// Build the ordered step list. An interstitial with position P shows AFTER P answered questions,
// i.e. just before question P (P === 0 → an intro card before Q1). Cards are keyed to the question
// numbering only: a card whose slot is past the last question of THIS flow (P >= question count)
// is simply DROPPED — short instruments must not end with every leftover visual in a row.
const buildSteps = (questions, interstitials) => {
  const qs = questions || [];
  const cards = (interstitials || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
  const steps = [];
  let ci = 0;
  const flushCardsUpTo = (slot) => {
    while (ci < cards.length && (cards[ci].position || 0) <= slot) {
      steps.push({ kind: 'interstitial', item: cards[ci] });
      ci += 1;
    }
  };
  qs.forEach((q, idx) => {
    flushCardsUpTo(idx);            // cards for "after idx questions" land just before question idx
    steps.push({ kind: 'question', q, qIndex: idx });
  });
  return steps;                     // leftover cards (position >= number of questions) are dropped
};

const BravosSurveyFlow = ({ questions, interstitials, answers, setAnswer, onComplete, header }) => {
  const { t } = useTranslation();
  const steps = useMemo(() => buildSteps(questions, interstitials), [questions, interstitials]);
  const [i, setI] = useState(0);

  const totalQ = (questions || []).length;

  const idx = steps.length ? Math.min(i, steps.length - 1) : 0;
  const step = steps[idx];
  const isLast = idx >= steps.length - 1;
  // A timed interstitial auto-advances after N seconds instead of showing a Next button.
  const timedSecs = step && step.kind === 'interstitial' ? (step.item.duration || 0) : 0;

  // Auto-advance timer for timed interstitials (re-armed whenever the step changes).
  useEffect(() => {
    if (timedSecs <= 0) return undefined;
    const id = setTimeout(() => {
      if (isLast) onComplete();
      else { setI(idx + 1); window.scrollTo(0, 0); }
    }, timedSecs * 1000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, timedSecs]);

  if (!steps.length) return null;

  const canNext = step.kind !== 'question'
    || !isRequired(step.q)
    || isAnswered(answers[step.q.id]);

  const next = () => {
    if (!canNext) return;
    if (isLast) { onComplete(); return; }
    setI(idx + 1);
    window.scrollTo(0, 0);
  };
  const back = () => { if (idx > 0) { setI(idx - 1); window.scrollTo(0, 0); } };

  // A card WITH an image is a FULL-SCREEN visual: the quote is baked into the image, so we show
  // it edge-to-edge with no logo and no separate text — just the image and a Next button (or an
  // auto-advance timer). Text-only quote cards fall through to the in-flow card below.
  if (step.kind === 'interstitial' && step.item.image_url) {
    return (
      <div className="bravos-full">
        <img src={step.item.image_url} alt="" className="bravos-full__img" />
        {timedSecs > 0 && (
          <div className="bravos-full__timer" aria-hidden="true">
            <div className="bravos-full__timerfill" key={idx}
                 style={{ animationDuration: `${timedSecs}s` }} />
          </div>
        )}
        <div className="bravos-full__nav">
          {idx > 0 && (
            <button type="button" className="bravos-submit bravos-submit--ghost" onClick={back}>
              <Icon name="arrow-left" /> {t('back')}
            </button>
          )}
          {/* Timed slides auto-advance, so they show no Next button. */}
          {timedSecs <= 0 && (
            <button type="button" className="bravos-submit" onClick={next}>
              {isLast
                ? (<>{t('surveyFinish')} <Icon name="arrow-right" /></>)
                : (<>{t('surveyNext')} <Icon name="arrow-right" /></>)}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Progress = share of question steps reached (interstitials don't count).
  const questionsReached = steps.slice(0, idx + 1).filter((s) => s.kind === 'question').length;
  const pct = totalQ ? Math.round((questionsReached / totalQ) * 100) : 0;

  return (
    <div className="bravos-flow">
      {/* Title/intro only on the first step, so every later question fits one mobile screen. */}
      {idx === 0 && header && <div className="bravos-flow__header">{header}</div>}

      <div className="bravos-flow__bar">
        <div className="bravos-flow__fill" style={{ width: `${pct}%` }} />
      </div>

      {step.kind === 'question' && (
        <>
          <p className="bravos-flow__count">
            {t('surveyQuestionOf', { current: step.qIndex + 1, total: totalQ })}
          </p>
          <BravosQuestions questions={[step.q]} answers={answers} setAnswer={setAnswer} />
        </>
      )}

      {step.kind === 'interstitial' && (
        <div className="bravos-interstitial">
          {step.item.quote && <p className="bravos-interstitial__quote">{step.item.quote}</p>}
          {step.item.author && <p className="bravos-interstitial__author">— {step.item.author}</p>}
          {timedSecs > 0 && (
            <div className="bravos-interstitial__timer" aria-hidden="true">
              <div className="bravos-interstitial__timerfill"
                   key={idx} style={{ animationDuration: `${timedSecs}s` }} />
            </div>
          )}
        </div>
      )}

      <div className="bravos-flow__nav">
        {idx > 0 && (
          <button type="button" className="bravos-submit bravos-submit--ghost" onClick={back}>
            <Icon name="arrow-left" /> {t('back')}
          </button>
        )}
        {/* Timed interstitials auto-advance, so they show no Next button. */}
        {timedSecs <= 0 && (
          <button type="button" className="bravos-submit" onClick={next} disabled={!canNext}>
            {isLast
              ? (<>{t('surveyFinish')} <Icon name="arrow-right" /></>)
              : (<>{t('surveyNext')} <Icon name="arrow-right" /></>)}
          </button>
        )}
      </div>
    </div>
  );
};

export default BravosSurveyFlow;
